import { useLoaderData } from 'react-router';
import { useSetAtom } from 'jotai';
import { useTracker } from '@archipro-website/tracker';

import { cn } from '@rocco/utils/cn';
import {
    VARIANT_ROW,
    VARIANT_ROW_SELECTED,
    VariantOptionMarker,
} from './purchase/variant-option-row';
import ProductAttributeEnum from '@modules/product/config/product-attribute';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { useVariantDecision } from '@modules/product/hook/use-variant-decision';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';
import type { DetailCreditColumn } from '~/ui-rocco/pages/detail-shared';
import { purchaseModalOpenAtom } from '../state/purchase-modal-atom';
import { useSizesInUse } from './purchase/purchase-flow-parts';

/** The names' own setting in the meta row: 12px uppercase, ink. */
const ENTRY_CLASS = VARIANT_ROW;
const SELECTED_CLASS = VARIANT_ROW_SELECTED;

/**
 * What decides the variant, as the meta row's columns beside the tags: the
 * colours as swatches, then each other attribute and the sizes as names
 * listed under their label, the way the project lists its team.
 *
 * Every item is the drawer's own pick: choosing one selects it, as the
 * drawer's selector would, and opens the drawer on the right so the reader
 * finishes the configuration where the price and the action are. A column
 * hides when the product has nothing to choose in it.
 */
export const useProductVariantColumns = (): DetailCreditColumn[] => {
    const { product } = useLoaderData<typeof loader>();
    const { attributeMap, decision, canonicalOptionIds } =
        useVariantOptionState();
    const sizesInUse = useSizesInUse();
    const variants = product.Variants ?? [];
    const { pickOption, pickSize } = useVariantDecision(variants);
    const openDrawer = useSetAtom(purchaseModalOpenAtom);
    const tracker = useTracker();

    const canonical = (id: number) => canonicalOptionIds.get(id) ?? id;
    const logPick = (id: number) =>
        tracker.log('DropDownSelect', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: { ExtraData: id },
        });

    // Each group once: a supplier can define the same group twice, and each
    // record became a column of its own.
    const allAttributes = [...attributeMap.values()];
    const signatureOf = (attribute: (typeof allAttributes)[number]) =>
        [
            attribute.Name,
            ...attribute.options.map((option) =>
                (option.Label ?? option.Code ?? '').toLowerCase().trim(),
            ),
        ].join('|');
    const attributes = allAttributes.filter(
        (attribute, index) =>
            allAttributes.findIndex(
                (other) => signatureOf(other) === signatureOf(attribute),
            ) === index,
    );

    const columns: DetailCreditColumn[] = attributes.map((attribute) => {
        const selectedId = decision.options[attribute.ID] ?? 0;
        const isColour = attribute.ID === ProductAttributeEnum.Colour;
        const isSelected = (optionId: number) =>
            selectedId !== 0 && canonical(selectedId) === canonical(optionId);
        const pick = (id: number) => {
            logPick(id);
            pickOption({ ID: id, attributeID: attribute.ID });
            openDrawer(true);
        };

        return {
            label: isColour ? 'Colours' : attribute.Name,
            entries: [],
            node: isColour ? (
                <ul className="m-0 flex list-none flex-wrap items-center gap-[0.5rem] p-0">
                    {attribute.options.map((option) => {
                        // A colourway with neither a texture nor a hex has
                        // nothing a circle can show — it drew as a blank
                        // chip. It stays pressable, carrying its name.
                        const swatch = option.Image?.Desktop
                            ? {
                                  backgroundImage: `url(${option.Image.Desktop})`,
                              }
                            : option.Code
                              ? { backgroundColor: option.Code }
                              : null;
                        return (
                            <li key={option.ID}>
                                {swatch ? (
                                    <TextTooltip content={option.Label}>
                                        <button
                                            type="button"
                                            aria-label={`Colour: ${option.Label}`}
                                            aria-pressed={isSelected(option.ID)}
                                            onClick={() => pick(option.ID)}
                                            className={cn(
                                                'block size-[1.5rem] shrink-0 cursor-pointer rounded-full border border-black/10 bg-cover bg-center p-0 transition-transform hover:scale-110',
                                                isSelected(option.ID) &&
                                                    'ring-1 ring-black ring-offset-2',
                                            )}
                                            style={swatch}
                                        />
                                    </TextTooltip>
                                ) : (
                                    <button
                                        type="button"
                                        aria-label={`Colour: ${option.Label}`}
                                        aria-pressed={isSelected(option.ID)}
                                        onClick={() => pick(option.ID)}
                                        className={cn(
                                            'inline-flex h-[1.5rem] cursor-pointer items-center rounded-full border border-black/15 bg-transparent px-[0.625rem] font-sans text-[0.75rem] leading-none text-[#323232]',
                                            isSelected(option.ID) &&
                                                'ring-1 ring-black ring-offset-2',
                                        )}
                                    >
                                        {option.Label}
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <ul className="m-0 flex list-none flex-col gap-0 p-0">
                    {attribute.options.map((option) => (
                        <li key={option.ID}>
                            <button
                                type="button"
                                aria-pressed={isSelected(option.ID)}
                                onClick={() => pick(option.ID)}
                                className={cn(
                                    ENTRY_CLASS,
                                    isSelected(option.ID) && SELECTED_CLASS,
                                )}
                            >
                                <VariantOptionMarker
                                    selected={isSelected(option.ID)}
                                />
                                {option.Label}
                            </button>
                        </li>
                    ))}
                </ul>
            ),
        };
    });

    if (sizesInUse.length > 0) {
        columns.push({
            label: 'Size',
            entries: [],
            node: (
                <ul className="m-0 flex list-none flex-col gap-0 p-0">
                    {sizesInUse.map((size) => (
                        <li key={size.ID}>
                            <button
                                type="button"
                                aria-pressed={decision.size === size.ID}
                                onClick={() => {
                                    logPick(size.ID);
                                    pickSize(size.ID);
                                    openDrawer(true);
                                }}
                                className={cn(
                                    ENTRY_CLASS,
                                    decision.size === size.ID && SELECTED_CLASS,
                                )}
                            >
                                <VariantOptionMarker
                                    selected={decision.size === size.ID}
                                />
                                {size.FullTitle}
                            </button>
                        </li>
                    ))}
                </ul>
            ),
        });
    }

    return columns;
};
